import React from 'react'
import { Box, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import { type ChartHeightPreset, ChartHeightPresets } from '../../../../interfaces/waveChart'

export interface ChartHeightPickerProps {
  value: ChartHeightPreset
  onChange: (preset: ChartHeightPreset) => void
  disabled?: boolean
  theme?: 'dark' | 'light'
}

export const ChartHeightPicker: React.FC<ChartHeightPickerProps> = ({
  value,
  onChange,
  disabled = false,
  theme = 'dark'
}) => {
  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: ChartHeightPreset | null,
  ) => {
    if (newValue !== null) {
      onChange(newValue)
    }
  }

  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={handleChange}
      size="small"
      disabled={disabled}
      sx={{
        height: '32px',
        '& .MuiToggleButton-root': {
          padding: '6px 10px',
          minWidth: '28px',
          height: '32px',
          border: '1px solid',
          borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
          backgroundColor: 'transparent',
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
          '&:hover': {
            backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.8)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.9)',
            }
          }
        }
      }}
    >
      {(Object.entries(ChartHeightPresets) as [ChartHeightPreset, { height: number; label: string }][]).map(([preset, config]) => {
        // Calculate relative height for the rectangle
        const maxHeight = 16
        const minHeight = 6
        const relativeHeight = minHeight + ((config.height - 200) / (800 - 200)) * (maxHeight - minHeight)
        
        return (
          <ToggleButton 
            key={preset} 
            value={preset} 
            aria-label={`Chart height: ${config.label}`}
          >
            <Tooltip title={`${config.label} (${config.height}px)`} placement="top">
              <Box
                sx={{
                  width: 10,
                  height: `${relativeHeight}px`,
                  backgroundColor: 'currentColor',
                  borderRadius: 1,
                  opacity: 0.9
                }}
              />
            </Tooltip>
          </ToggleButton>
        )
      })}
    </ToggleButtonGroup>
  )
} 